import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Share2, Download } from 'lucide-react';
import { toast } from 'sonner';

export const ShareCardDialog = ({ open, onClose, profile, stats }) => {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const wins = stats?.total_wins ?? profile?.total_wins ?? 0;
  const losses = stats?.total_losses ?? profile?.total_losses ?? 0;
  const winPct = wins + losses > 0 ? Math.round((wins / (wins + losses)) * 100) : 0;

  const renderImage = async () => {
    const canvas = await html2canvas(cardRef.current, { scale: 2, backgroundColor: null, useCORS: true });
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  };

  const handleShare = async () => {
    setBusy(true);
    try {
      const blob = await renderImage();
      const file = new File([blob], 'tennis-pro-card.png', { type: 'image/png' });
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Tennis Pro Israel',
          text: `הכרטיס שלי ב-Tennis Pro Israel 🎾 בואו לשחק! ${window.location.origin}`,
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'tennis-pro-card.png';
        a.click();
        URL.revokeObjectURL(url);
        toast.success('הכרטיס הורד! אפשר לשתף אותו בוואטסאפ/אינסטגרם 🎾');
      }
    } catch (e) {
      if (e.name !== 'AbortError') toast.error('שגיאה ביצירת הכרטיס');
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      const blob = await renderImage();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tennis-pro-card.png';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('הכרטיס הורד בהצלחה! 📥');
    } catch {
      toast.error('שגיאה ביצירת הכרטיס');
    } finally {
      setBusy(false);
    }
  };

  if (!profile) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" dir="rtl" data-testid="share-card-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">🎾 כרטיס השחקן שלך</DialogTitle>
          <DialogDescription>שתף לסטורי, לוואטסאפ או הורד כתמונה</DialogDescription>
        </DialogHeader>

        {/* הכרטיס עצמו */}
        <div className="flex justify-center">
          <div
            ref={cardRef}
            dir="rtl"
            style={{
              width: 320,
              borderRadius: 24,
              overflow: 'hidden',
              background: 'linear-gradient(160deg, #1e2b5e 0%, #17224d 55%, #0d3b2e 100%)',
              padding: 24,
              color: '#fff',
              fontFamily: 'inherit',
              position: 'relative',
            }}
            data-testid="share-card-canvas"
          >
            <div style={{ position: 'absolute', top: -60, left: -60, width: 180, height: 180, borderRadius: '50%', background: 'rgba(29,111,242,0.35)', filter: 'blur(40px)' }}></div>
            <div style={{ position: 'absolute', bottom: -60, right: -60, width: 180, height: 180, borderRadius: '50%', background: 'rgba(16,180,126,0.3)', filter: 'blur(40px)' }}></div>

            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontWeight: 900, fontSize: 16, direction: 'ltr' }}>
                <span style={{ color: '#6ea8ff' }}>Tennis</span><span style={{ color: '#3ddfa9' }}>Pro</span> <span style={{ fontSize: 11, opacity: 0.7 }}>ISRAEL 🇮🇱</span>
              </div>
              <span style={{ fontSize: 22 }}>🎾</span>
            </div>

            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
              <div style={{ width: 72, height: 72, borderRadius: '50%', border: '3px solid #3ddfa9', overflow: 'hidden', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {profile.avatar ? (
                  <img src={profile.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} crossOrigin="anonymous" />
                ) : (
                  <span style={{ fontSize: 30, color: '#1e2b5e', fontWeight: 900 }}>{(profile.full_name || '?').charAt(0)}</span>
                )}
              </div>
              <div>
                <div style={{ fontWeight: 900, fontSize: 20, lineHeight: 1.2 }}>{profile.full_name}</div>
                <div style={{ fontSize: 13, color: '#6ea8ff', fontWeight: 700 }}>{profile.skill_level || 'שחקן טניס'}</div>
                {profile.city && <div style={{ fontSize: 11, opacity: 0.7 }}>📍 {profile.city}</div>}
              </div>
            </div>

            <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                { label: 'דירוג ארצי', value: profile.rank ? `#${profile.rank}` : '—' },
                { label: 'נקודות', value: profile.total_points || 0 },
                { label: 'ניצחונות', value: wins },
                { label: 'אחוז ניצחונות', value: `${winPct}%` },
              ].map((s) => (
                <div key={s.label} style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 14, padding: '10px 12px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.12)' }}>
                  <div style={{ fontWeight: 900, fontSize: 20, color: '#3ddfa9' }}>{s.value}</div>
                  <div style={{ fontSize: 10, opacity: 0.75, fontWeight: 700 }}>{s.label}</div>
                </div>
              ))}
            </div>

            <div style={{ position: 'relative', marginTop: 16, textAlign: 'center', fontSize: 11, opacity: 0.8, fontWeight: 700 }}>
              בואו לשחק איתי 🎾 tennisproisrael.co.il
            </div>
          </div>
        </div>

        <div className="flex gap-2 pt-1">
          <Button onClick={handleShare} disabled={busy} className="flex-1 bg-gradient-to-l from-blue-600 to-green-600 rounded-full font-bold" data-testid="share-card-share-btn">
            <Share2 className="w-4 h-4 ml-2" />
            {busy ? 'מכין...' : 'שתף'}
          </Button>
          <Button onClick={handleDownload} disabled={busy} variant="outline" className="rounded-full font-bold" data-testid="share-card-download-btn">
            <Download className="w-4 h-4 ml-2" />
            הורד
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
